import {
	Component,
	Input,
	ChangeDetectionStrategy,
	inject,
	output,
	input,
	InputSignal,
	OutputEmitterRef
} from '@angular/core';
import { ReminderTaskData, TaskReminder, ReminderAction, ReminderConfig } from '@app/pages/tasks/models/tasks.model';
import { format } from 'date-fns';
import { hour24Format, Languages, MM_DD_YYYY, DD_MM_YYYY } from '@core';
import { TranslateService } from '@ngx-translate/core';
import { SettingsService } from '@app/core/services/settings/settings.service';
import { Params } from '@angular/router';
import { ReminderActionsEnum } from '../../../enums/tasks.enums';

@Component({
	selector: 'task-reminder',
	templateUrl: './task-reminder.component.html',
	styleUrls: ['./task-reminder.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class TaskReminderComponent {
	private translateService = inject(TranslateService);
	private settingsService = inject(SettingsService);

	@Input({ required: true })
	set reminder(data: TaskReminder) {
		this._reminder = data;
		this.reminderConfig = this.mapReminderDataToConfig(data);
	}
	get reminder(): TaskReminder | null {
		return this._reminder;
	}
	readonly taskData: InputSignal<ReminderTaskData> = input.required<ReminderTaskData>();
	@Input() isEditMode: boolean = false;

	readonly delete: OutputEmitterRef<number> = output<number>();
	readonly update: OutputEmitterRef<{ reminderId: number; formData: Params }> = output<{
		reminderId: number;
		formData: Params;
	}>();

	private _reminder: TaskReminder = null;
	private readonly dateFormat: string;
	private readonly currentLang: Languages;
	private readonly reminderActions: ReminderAction[] = [
		{
			id: ReminderActionsEnum.Edit,
			icon: 'pencil',
			tooltip: 'MODALS.TASK.REMINDERS.EDIT'
		},
		{
			id: ReminderActionsEnum.Delete,
			icon: 'trash',
			tooltip: 'MODALS.TASK.REMINDERS.DELETE'
		}
	];

	public readonly reminderActionsEnum = ReminderActionsEnum;
	public reminderConfig: ReminderConfig;

	constructor() {
		this.currentLang = this.translateService.currentLang as Languages;
		this.dateFormat = this.settingsService.isUsFirm() ? MM_DD_YYYY : DD_MM_YYYY;
	}

	private mapReminderDataToConfig(reminder: TaskReminder): ReminderConfig {
		return {
			id: reminder.id,
			timestamp: format(reminder.created_at, hour24Format) + ' | ' + format(reminder.created_at, this.dateFormat),
			user: this.currentLang === Languages.HE ? reminder.user.alt_name : reminder.user.name,
			actions: reminder.completed
				? this.reminderActions.filter((action: ReminderAction) => action.id !== ReminderActionsEnum.Edit)
				: this.reminderActions,
			details: reminder.details
		};
	}

	public onReminderAction(action: ReminderActionsEnum, formData?: Params): void {
		switch (action) {
			case ReminderActionsEnum.Save:
				this.isEditMode = false;
				this.update.emit({ reminderId: this.reminder.id, formData });
				break;
			case ReminderActionsEnum.Edit:
				this.isEditMode = true;
				break;
			case ReminderActionsEnum.Delete:
				this.delete.emit(this.reminder.id);
				break;
			case ReminderActionsEnum.Cancel:
				this.isEditMode = false;
				break;
		}
	}
}
